import React from "react";
import type { ExperienceItem } from "types";

interface Props {
  experience: ExperienceItem;
}

const ExperienceCard: React.FC<Props> = ({ experience }) => {
  const companyInitial = experience.company?.charAt(0) || "C";

  return (
    <article className="group relative w-full border border-border bg-bg-elevated rounded-lg p-6 md:p-8 transition-all duration-300 hover:border-border-hover hover:shadow-card-hover">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-accent via-accent/50 to-transparent" />

      <div className="relative flex flex-col gap-6 md:flex-row md:items-start">
        {/* Logo / Initial */}
        <div className="relative flex-shrink-0">
          <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 border border-border bg-bg-surface rounded transition-all duration-300 group-hover:border-accent/20">
            {experience.logo_path ? (
              <img
                className="object-contain w-10 h-10 md:w-12 md:h-12"
                src={require(`../../assests/images/${experience.logo_path}`)}
                alt={experience.company}
              />
            ) : (
              <span className="text-2xl md:text-3xl font-display font-bold text-accent">
                {companyInitial}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 w-full space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono tracking-[0.12em] uppercase border border-accent/20 rounded text-accent bg-accent-dim">
                <span className="w-1 h-1 rounded-full bg-accent" />
                Experience
              </span>

              <h3 className="text-xl md:text-2xl font-display font-bold tracking-tight text-text-primary">
                {experience.title}
              </h3>

              {experience.company_url ? (
                <a
                  href={experience.company_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                >
                  {experience.company}
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              ) : (
                <p className="text-sm font-medium text-accent">
                  {experience.company}
                </p>
              )}
            </div>

            <div className="flex flex-col items-start gap-2 md:items-end">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono border border-accent/20 rounded text-accent bg-accent-dim">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                {experience.duration}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono border border-border rounded text-text-muted bg-bg-surface">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                {experience.location}
              </span>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-text-secondary border-l-2 border-accent/20 pl-4">
            {experience.description}
          </p>
        </div>
      </div>
    </article>
  );
};

export default ExperienceCard;
